import React from 'react';
import PropTypes from 'prop-types';
import { safeRest } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
var PROGRESS_BAR_COLORS = Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
var PROGRESS_CIRCLE_COLORS = PROGRESS_BAR_COLORS;
var PROGRESS_CIRCLE_SIZES = Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
var useStyles = createUseStyles(function (theme) {
  var spacing = theme.spacing,
    palette = theme.palette;
  var width = function width(s) {
    return {
      xs: spacing[4],
      sm: spacing[6],
      md: spacing[7],
      lg: spacing[9]
    }[s];
  };
  var height = width;
  var rotate = {
    '@keyframes rotate': {
      '100%': {
        transform: 'rotate(360deg)'
      }
    }
  };
  var stretch = {
    '@keyframes stretch': {
      '0%': {
        strokeDasharray: '339.292, 339.292',
        strokeDashoffset: '203.57519999999997',
        animationTimingFunction: 'cubic-bezier(0.3, 0, 1, 1)'
      },
      '40%': {
        strokeDasharray: '339.292, 339.292',
        strokeDashoffset: '253.57519999999997',
        animationTimingFunction: 'cubic-bezier(0, 0, 0.9, 1)'
      },
      '65%': {
        strokeDashoffset: '303.57519999999997'
      },
      '100%': {
        strokeDashoffset: '203.57519999999997'
      }
    }
  };
  return _objectSpread(_objectSpread(_objectSpread({}, rotate), stretch), {}, {
    wrenchProgressCircle: function wrenchProgressCircle(_ref) {
      var size = _ref.size,
        continuous = _ref.continuous;
      return {
        display: 'inline-block',
        position: 'relative',
        width: width(size),
        height: height(size),
        fill: 'none',
        '-webkit-transform': continuous && 'translate3d(0,0,0)'
      };
    },
    wrenchCirclePath: function wrenchCirclePath() {
      return {
        transform: 'rotate(-90deg)'
      };
    },
    wrenchCirclePathGroup: function wrenchCirclePathGroup(_ref2) {
      var continuous = _ref2.continuous;
      return {
        animation: continuous && '$rotate 1s linear infinite',
        transformOrigin: continuous && 'center center'
      };
    },
    wrenchCirclePathBorder: function wrenchCirclePathBorder() {
      return {
        stroke: palette.muted.main
      };
    },
    wrenchCirclePathFill: function wrenchCirclePathFill(_ref3) {
      var color = _ref3.color,
        continuous = _ref3.continuous;
      return {
        stroke: palette[color].main,
        strokeLinecap: 'round',
        strokeDasharray: continuous && '339.292, 339.292',
        strokeDashoffset: continuous && '203.57519999999997',
        animation: continuous && '$stretch 1s infinite',
        transformOrigin: continuous && 'center center'
      };
    },
    wrenchCircleDisplay: function wrenchCircleDisplay(_ref4) {
      var size = _ref4.size;
      return {
        position: 'absolute',
        top: '0',
        left: '0',
        width: width(size),
        height: height(size),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      };
    }
  });
});
var _excluded = ["children", "size", "percent", "color", "continuous", "valueMax", "valueMin", "valueText"];
var CIRCLE_CIRCUMFERENCE = 339.292;
/*
 * @todo need a title / aria-label attached to progress bar
 * https://dequeuniversity.com/rules/axe/4.1/aria-progressbar-name?application=axeAPI
 */

var ProgressCircle = function ProgressCircle(_ref) {
  var children = _ref.children,
    size = _ref.size,
    percent = _ref.percent,
    color = _ref.color,
    continuous = _ref.continuous,
    valueMax = _ref.valueMax,
    valueMin = _ref.valueMin,
    valueText = _ref.valueText,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var classes = useStyles({
    color: color,
    size: size,
    continuous: continuous,
    theme: theme
  });
  var circleOptions = {
    lg: {
      strokeWidth: 8,
      radius: 56
    },
    sm: {
      strokeWidth: 16,
      radius: 52
    },
    xs: {
      strokeWidth: 20,
      radius: 50
    }
  };
  var _ref2 = circleOptions[size] || {
      strokeWidth: 12,
      radius: 54
    },
    strokeWidth = _ref2.strokeWidth,
    radius = _ref2.radius;
  var valueNow = percent ? parseFloat(percent) * 100 : undefined;
  var strokeDasharray = !continuous ? "".concat(CIRCLE_CIRCUMFERENCE, " ").concat(CIRCLE_CIRCUMFERENCE) : undefined;
  var strokeDashoffset = !continuous ? CIRCLE_CIRCUMFERENCE * (1 - percent) : undefined;
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: classes.wrenchProgressCircle,
    role: "progressbar",
    "aria-valuenow": valueNow,
    "aria-valuemax": valueMax,
    "aria-valuemin": valueMin,
    "aria-valuetext": valueText
  }, safeRest(rest)), /*#__PURE__*/React.createElement("svg", {
    className: classes.wrenchCirclePath,
    role: "img",
    "aria-label": "Progress",
    viewBox: "0 0 120 120"
  }, /*#__PURE__*/React.createElement("g", {
    className: classes.wrenchCirclePathGroup
  }, /*#__PURE__*/React.createElement("circle", {
    className: classes.wrenchCirclePathBorder,
    cx: "60",
    cy: "60",
    r: radius,
    strokeWidth: strokeWidth
  }), /*#__PURE__*/React.createElement("circle", {
    className: classes.wrenchCirclePathFill,
    cx: "60",
    cy: "60",
    r: radius,
    strokeWidth: strokeWidth,
    strokeDasharray: strokeDasharray,
    strokeDashoffset: strokeDashoffset
  }))), children && /*#__PURE__*/React.createElement("div", {
    className: classes.wrenchCircleDisplay
  }, children));
};
ProgressCircle.displayName = "ProgressCircle";
ProgressCircle.propTypes = {
  /**
   * The progress can wrap around content such as text, numbers, icons
   */
  children: PropTypes.node,
  /**
   * Percentage of current progress
   */
  percent: PropTypes.number,
  /**
   * Size variants
   */
  size: PropTypes.oneOf(PROGRESS_CIRCLE_SIZES),
  /**
   * Color variants
   */
  color: PropTypes.oneOf(PROGRESS_CIRCLE_COLORS),
  /**
   * Continuous percent for indeterminate progress
   */
  continuous: PropTypes.bool,
  /**
   * Maximum value number
   */
  valueMax: function valueMax(props, propName) {
    if (props[propName] && typeof props[propName] !== 'number') {
      return new Error("Invalid prop type ".concat(propName, " supplied. Should be type number"));
    }
    if (props.percent && typeof props[propName] === 'undefined') {
      return new Error("If the prop percent is used, ".concat(propName, " is required"));
    }
  },
  /**
   * Minimum value number
   */
  valueMin: function valueMin(props, propName) {
    if (props[propName] && typeof props[propName] !== 'number') {
      return new Error("Invalid prop type ".concat(propName, " supplied. Should be type number"));
    }
    if (props.percent && typeof props[propName] === 'undefined') {
      return new Error("If the prop percent is used, ".concat(propName, " is required"));
    }
  },
  /**
   * Aria value text - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_progressbar_role
   */
  valueText: function valueText(props, propName) {
    if (props[propName] && typeof props[propName] !== 'string') {
      return new Error("Invalid prop type ".concat(propName, " supplied should be type string"));
    }
    if (props.continuous && typeof props[propName] === 'undefined') {
      return new Error("If the value of the progress bar is indeterminate, ".concat(propName, " is required"));
    }
  }
};
ProgressCircle.defaultProps = {
  children: undefined,
  percent: undefined,
  color: 'success',
  size: 'md',
  continuous: false,
  valueMax: undefined,
  valueMin: undefined,
  valueText: undefined
};
ProgressCircle.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "ProgressCircle",
  "props": {
    "children": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "The progress can wrap around content such as text, numbers, icons"
    },
    "percent": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Percentage of current progress"
    },
    "color": {
      "defaultValue": {
        "value": "'success'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "PROGRESS_CIRCLE_COLORS"
      },
      "required": false,
      "description": "Color variants"
    },
    "size": {
      "defaultValue": {
        "value": "'md'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "PROGRESS_CIRCLE_SIZES"
      },
      "required": false,
      "description": "Size variants"
    },
    "continuous": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Continuous percent for indeterminate progress"
    },
    "valueMax": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "custom",
        "raw": "(props, propName) => {\n  if (props[propName] && typeof props[propName] !== 'number') {\n    return new Error(\n      `Invalid prop type ${propName} supplied. Should be type number`\n    );\n  }\n  if (props.percent && typeof props[propName] === 'undefined') {\n    return new Error(`If the prop percent is used, ${propName} is required`);\n  }\n}"
      },
      "required": false,
      "description": "Maximum value number"
    },
    "valueMin": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "custom",
        "raw": "(props, propName) => {\n  if (props[propName] && typeof props[propName] !== 'number') {\n    return new Error(\n      `Invalid prop type ${propName} supplied. Should be type number`\n    );\n  }\n  if (props.percent && typeof props[propName] === 'undefined') {\n    return new Error(`If the prop percent is used, ${propName} is required`);\n  }\n}"
      },
      "required": false,
      "description": "Minimum value number"
    },
    "valueText": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "custom",
        "raw": "(props, propName) => {\n  if (props[propName] && typeof props[propName] !== 'string') {\n    return new Error(\n      `Invalid prop type ${propName} supplied should be type string`\n    );\n  }\n  if (props.continuous && typeof props[propName] === 'undefined') {\n    return new Error(\n      `If the value of the progress bar is indeterminate, ${propName} is required`\n    );\n  }\n}"
      },
      "required": false,
      "description": "Aria value text - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_progressbar_role"
    }
  }
};
export { ProgressCircle };